import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { LocationSource } from '../../api/constants';
import hasValidLocation from '../lib/hasValidLocation';
import maybeIsExcludedLocation from '../lib/maybeIsExcludedLocation';
import type {
  DeliveryApi,
  Errors,
  GeoApi,
  LocationChangeCallback,
  LocationContextType,
  LocationData,
  State
} from '../types';

const defaultErrorState: Errors = {
  deliveryError: false,
  locationError: false
};

const defaultState: State = {
  city: '',
  state: '',
  zip: ''
};

const defaultContext: LocationContextType = {
  ...defaultState,
  errorState: defaultErrorState,
  isExcludedLocation: false,
  onCloseHandler: () => {},
  setGeolocation: (_zipcode: string) =>
    new Promise<LocationData>(resolve =>
      resolve({
        city: '',
        source: undefined,
        state: '',
        zip: ''
      })
    )
};

const LocationContext = createContext<LocationContextType>(defaultContext);

export const useLocationContext = () => useContext(LocationContext);

type Props = {
  children: React.ReactNode;
  fetchDelivery: DeliveryApi;
  geoApi: GeoApi;
  isOpen?: boolean;
  onCloseHandler: () => void;
  onUserLocationChange?: LocationChangeCallback;
};

export default function LocationContextProvider({
  children,
  fetchDelivery,
  geoApi,
  isOpen,
  onCloseHandler,
  onUserLocationChange
}: Props) {
  const [location, setLocation] = useState<State>(defaultState);
  const [errorState, setErrorState] = useState<Errors>(defaultErrorState);

  useEffect(() => {
    setErrorState(defaultErrorState);
  }, [isOpen]);

  const setGeolocation = useCallback(
    async (zipcode: string) => {
      const locationToSave: State = {
        city: '',
        state: '',
        zip: ''
      };
      const errors: Errors = {
        locationError: false,
        deliveryError: false
      };

      try {
        const res = await geoApi.setGeoLocation(zipcode);
        if (res.source === LocationSource.Default) {
          errors.locationError = true;
        } else {
          locationToSave.city = res.city;
          locationToSave.state = res.state;
          locationToSave.zip = res.zip;
        }
      } catch (err) {
        errors.locationError = true;
      }

      try {
        if (!errors.locationError) {
          const { pickUpInfoToDisplay, isMeetupAllowed, isDeliveryAllowed } =
            await fetchDelivery(zipcode);
          locationToSave.pickUpInfoToDisplay = pickUpInfoToDisplay;
          locationToSave.isMeetupAllowed = isMeetupAllowed;
          locationToSave.isDeliveryAllowed = isDeliveryAllowed;
        }
      } catch (err) {
        errors.deliveryError = true;
      }

      setErrorState(errors);

      if (!hasValidLocation(locationToSave)) {
        throw new Error('Location is invalid');
      }

      if (onUserLocationChange) {
        onUserLocationChange({
          location: locationToSave,
          errorState: errors
        });
      }

      setLocation(locationToSave);

      return locationToSave;
    },
    [geoApi, fetchDelivery, onUserLocationChange]
  );

  useEffect(() => {
    (async () => {
      const locationToSave: State = {
        city: '',
        state: '',
        zip: ''
      };
      const errors: Errors = {
        locationError: false,
        deliveryError: false
      };

      try {
        const res = await geoApi.getGeoLocation();
        locationToSave.city = res.city;
        locationToSave.state = res.state;
        locationToSave.zip = res.zip;
      } catch (err) {
        errors.locationError = true;
      }

      try {
        if (!errors.locationError) {
          const { pickUpInfoToDisplay, isMeetupAllowed, isDeliveryAllowed } =
            await fetchDelivery(locationToSave.zip);
          locationToSave.pickUpInfoToDisplay = pickUpInfoToDisplay;
          locationToSave.isMeetupAllowed = isMeetupAllowed;
          locationToSave.isDeliveryAllowed = isDeliveryAllowed;
        }
      } catch (err) {
        errors.deliveryError = true;
      }

      if (onUserLocationChange) {
        onUserLocationChange({
          location: locationToSave,
          errorState: errors
        });
      }

      setErrorState(errors);
      setLocation(locationToSave);
    })();
  }, [fetchDelivery, geoApi, onUserLocationChange]);

  const isExcludedLocation = maybeIsExcludedLocation(location);

  const value = useMemo<LocationContextType>(
    () => ({
      ...location,
      errorState,
      isExcludedLocation,
      onCloseHandler,
      setGeolocation
    }),
    [errorState, isExcludedLocation, location, onCloseHandler, setGeolocation]
  );

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

LocationContextProvider.displayName = 'LocationContextProvider';
